import { useMemo } from 'react';
import { Button, Column, Row, Typography } from '@tempmee/react';
import { DentalOfficeShiftCard, type DentalOfficeShiftCardMenuItem } from '@tempmee/react-blocks';
import { DotsMenu, MenuItem } from '~/commonComponents/DotsMenu';
import { useNavigateToCreateInterview } from '~/components/PermanentJobsV2/CreateOnsiteInterview/hooks/useNavigateToCreateInterview';
import { InviteToExistingInterviewModal } from '~/components/PermanentJobsV2/JobOverview/components/CandidateTabView/CandidateListTable/InviteToExistingInterviewModal';
import {
  CandidateStatus,
  OnsiteInterviewStatus,
  useGetInvitableOnsiteInterviews,
} from '~/domain/PermanentJobs';
import { useSelectedOffice } from '~/hooks/useSelectedOffice';
import { useToggle } from '~/hooks/useToggle';
import { CANDIDATE_DETAILS_COPY, sortInterviewsNewestFirst } from '../../candidateDetailsView';
import { OnsiteInterviewActionModals } from '../../components/OnsiteInterviewActionModals';
import { useOnsiteInterviewActions } from '../../hooks/useOnsiteInterviewActions';
import type { TOnsiteInterviewsListProps } from './interfaces';
import { mapCandidateInterviewToShiftCard } from './mapCandidateInterviewToShiftCard';

/** The face of the invite menu's trigger; a module-level component so DotsMenu never remounts it. */
const InviteMenuTrigger = ({ isDisabled }: { isDisabled: boolean }) => (
  <Button
    variant="outlined"
    size="small"
    tabIndex={-1}
    disabled={isDisabled}
    data-cy="candidate-action-invite-to-interview"
  >
    {CANDIDATE_DETAILS_COPY.inviteToInterview}
  </Button>
);

/**
 * The Candidate Details interviews column (GOTU-8148): every one of the
 * candidate's interviews as a DS shift card, newest first. "Invite to interview"
 * opens the same Existing / New Interview menu as the job's candidate table, and
 * "Add interview" under the cards goes straight to a new interview. The invite
 * is greyed out while the posting has no interviews to invite to, which leaves
 * "Add interview" as the way in. A hired or rejected candidate gets neither,
 * and an empty column then says so instead.
 */
export const OnsiteInterviewsList = ({
  postingId,
  maps,
  officeId,
  officeStatus,
  professionalId,
  professionalName,
}: TOnsiteInterviewsListProps) => {
  const [isInviteOpen, inviteControls] = useToggle();
  const navigateToCreateInterview = useNavigateToCreateInterview();
  const { office_name: officeName = '' } = useSelectedOffice(officeId);
  const actions = useOnsiteInterviewActions({ maps, officeId, postingId, professionalId });
  const { handleDeclineInterview } = actions.decline;

  const sortedInterviews = useMemo(() => sortInterviewsNewestFirst(Object.values(maps)), [maps]);

  const isTerminalStatus =
    officeStatus === CandidateStatus.Hired || officeStatus === CandidateStatus.Rejected;
  const canInvite = !isTerminalStatus;
  const { data: invitableInterviews } = useGetInvitableOnsiteInterviews(
    { postingId, professionalId },
    { enabled: canInvite },
  );
  const hasInterviewsToInvite = invitableInterviews.length > 0;

  const handleNewInterview = () =>
    navigateToCreateInterview({ jobPostingId: postingId, professionalId, professionalName });

  return (
    <>
      <Column gap={2} data-testid="onsite-interviews-list">
        <Row align="center" justify="between" px={2}>
          <Typography variant="bodyLarge" bold as="h2" className="text-text-secondary">
            {CANDIDATE_DETAILS_COPY.interviewsHeading}
          </Typography>
          {canInvite && (
            // The same menu as the candidate table's InviteColumnCell. DotsMenu
            // owns the open state and its own trigger button, so the DS Button
            // is the trigger's face: it is kept out of the tab order, and a
            // click on it bubbles to DotsMenu.
            <DotsMenu
              dataCy="candidate-action-invite-menu"
              position="bottom-right"
              disabled={!hasInterviewsToInvite}
              CustomMenuIcon={InviteMenuTrigger}
            >
              <MenuItem
                dataCy="existing-interview"
                text="Existing Interview"
                onClick={inviteControls.setOn}
              />
              <MenuItem dataCy="new-interview" text="New Interview" onClick={handleNewInterview} />
            </DotsMenu>
          )}
        </Row>
        {sortedInterviews.length === 0 && !canInvite && (
          <Typography as="p" variant="body2" className="text-text-secondary" textAlign="center">
            {CANDIDATE_DETAILS_COPY.noInterviews}
          </Typography>
        )}
        {sortedInterviews.map((interview) => {
          const id = interview.onsiteInterviewId;
          const { counterOffer } = interview;
          const menuItems: DentalOfficeShiftCardMenuItem[] | undefined =
            interview.onsiteInterviewStatus === OnsiteInterviewStatus.ActionNeeded && counterOffer
              ? [
                  {
                    label: 'Decline',
                    testID: 'candidate-action-decline',
                    onSelect: () => handleDeclineInterview(id, counterOffer),
                  },
                ]
              : undefined;

          return (
            <DentalOfficeShiftCard
              key={id}
              data-cy={`onsite-interview-card-${id}`}
              {...mapCandidateInterviewToShiftCard(interview, officeName)}
              menuItems={menuItems}
              onCardClick={() => actions.handleViewDetails(id)}
              onMessage={
                interview.onsiteInterviewStatus === OnsiteInterviewStatus.Scheduled
                  ? () => actions.handleMessageClick(id)
                  : undefined
              }
            />
          );
        })}
        {canInvite && (
          <Row justify="center">
            <Button
              variant="text"
              color="primary"
              size="small"
              onClick={handleNewInterview}
              data-cy="candidate-action-add-interview"
            >
              {CANDIDATE_DETAILS_COPY.addInterview}
            </Button>
          </Row>
        )}
      </Column>

      <OnsiteInterviewActionModals
        actions={actions}
        maps={maps}
        professionalId={professionalId}
        professionalName={professionalName}
      />

      <InviteToExistingInterviewModal
        isOpen={isInviteOpen}
        jobPostingId={postingId}
        officeId={officeId}
        professionalId={professionalId}
        onClose={inviteControls.setOff}
      />
    </>
  );
};
